import { useEffect, useMemo, useState } from "react";
import { getAllUsedCatalogs } from "../../apis";
import useLocalizedLabel from "../../hooks/useLocalizedLabel";

/** Prefijo de los filtros de catálogo en la URL: cat_EG::RC-100=grievance */
export const CATALOGUE_PREFIX = "cat_";

/**
 * La llave del catálogo va literal en el parámetro. Así la URL se puede leer y
 * volver a escribir sin tener cargados los catálogos: hace falta el catálogo
 * para pintar la etiqueta, no para conservar el filtro.
 */
export const toParamKey = (rootKey) => `${CATALOGUE_PREFIX}${rootKey}`;

export const fromParamKey = (paramKey) =>
  paramKey.slice(CATALOGUE_PREFIX.length);

// Los catálogos del formulario no cambian durante la sesión
let cache = null;

/**
 * Campos del formulario que declaran `catalogue`, convertidos en filtros del
 * buscador. Los trae /api/search/last_form_catalogues/, que ya recorre los
 * stepers del formulario vigente y devuelve el árbol de cada catálogo.
 *
 * Solo se expone el primer nivel: el backend ancla con `^path($|::)`, así que
 * elegir la rama padre ya arrastra todo lo que cuelga de ella.
 */
export const useCatalogueFilters = ({ enabled = true } = {}) => {
  const getLocalizedLabel = useLocalizedLabel();
  const [catalogues, setCatalogues] = useState(cache || {});
  const [isLoading, setIsLoading] = useState(enabled && !cache);

  useEffect(() => {
    if (!enabled || cache) return;

    let cancelled = false;
    setIsLoading(true);

    getAllUsedCatalogs()
      .then((resp) => {
        cache = resp || {};
        if (!cancelled) setCatalogues(cache);
      })
      .catch((error) => {
        // Sin catálogos el buscador sigue sirviendo, solo pierde esas secciones
        console.error(error);
        if (!cancelled) setCatalogues({});
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const filters = useMemo(
    () =>
      Object.entries(catalogues)
        .map(([rootKey, root]) => {
          const options = Object.entries(root?.type || {})
            .filter(([, node]) => node?.is_active !== false)
            .map(([value, node]) => ({
              value,
              label: getLocalizedLabel(node) || value,
            }));

          return {
            key: toParamKey(rootKey),
            rootKey,
            label: getLocalizedLabel(root) || rootKey.split("::")[1],
            multiple: true,
            isCatalogue: true,
            options,
          };
        })
        // Un catálogo sin opciones activas no aporta una sección al panel
        .filter((filter) => filter.options.length > 0),
    [catalogues, getLocalizedLabel]
  );

  return { filters, isLoading };
};

/**
 * Selecciones de catálogo -> parámetro `catalog` del endpoint.
 * {"cat_EG::RC-100": ["grievance"]} -> "EG::RC-100::grievance"
 *
 * Se resuelve desde la propia llave del parámetro, así que funciona aunque los
 * catálogos todavía no se hayan cargado.
 */
export const buildCatalogParam = (advanced = {}) => {
  const selections = [];

  Object.entries(advanced).forEach(([key, values]) => {
    if (!key.startsWith(CATALOGUE_PREFIX) || !Array.isArray(values)) return;

    const rootKey = fromParamKey(key);
    values.forEach((value) => selections.push(`${rootKey}::${value}`));
  });

  return selections.join(";");
};
